/**
 * The durability nag.
 *
 * It appears when one of the three legs is missing and says which one.
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Button, LinkButton } from '../components/Button';
import { springStandard } from '../design/motion';
import { useVaultState } from '../hooks/useVaultState';
import {
  canPromptInstall,
  captureInstallPrompt,
  isInstalledAsPWA,
  promptInstall,
  requestPersistence,
  type PersistenceStatus,
} from '../vault/persistence';
import { useLanguage } from '../i18n/context';

export function PersistenceBanner() {
  const vaultState = useVaultState();
  const { t } = useLanguage();
  const [status, setStatus] = useState<PersistenceStatus | null>(null);
  const [installable, setInstallable] = useState(false);

  useEffect(() => {
    const stop = captureInstallPrompt();
    void requestPersistence().then(setStatus);
    const timer = setTimeout(() => setInstallable(canPromptInstall()), 1500);
    return () => {
      stop();
      clearTimeout(timer);
    };
  }, []);

  if (!vaultState.initialised) return null;

  const noExport = vaultState.exportedAt === null;
  const notInstalled = !isInstalledAsPWA() && installable;
  const notPersisted = status !== null && status.supported && !status.persisted;

  if (!noExport && !notInstalled && !notPersisted) return null;

  return (
    <AnimatePresence>
      <motion.aside
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={springStandard}
        className="material-card mb-6 rounded-(--radius-card) p-4"
      >
        {noExport ? (
          <>
            <h2 className="text-title text-ink-7">{t.persistence.noBackupTitle}</h2>
            <p className="text-body mt-1 mb-4 text-ink-5">
              {t.persistence.noBackupBody}
            </p>
            <LinkButton size="md" className="w-full" to="/settings">
              {t.persistence.noBackupCta}
            </LinkButton>
          </>
        ) : notInstalled ? (
          <>
            <h2 className="text-title text-ink-7">{t.persistence.installTitle}</h2>
            <p className="text-body mt-1 mb-4 text-ink-5">
              {t.persistence.installBody}
            </p>
            <Button size="md" className="w-full" onClick={() => void promptInstall()}>
              {t.persistence.installCta}
            </Button>
          </>
        ) : (
          <>
            <h2 className="text-title text-ink-7">{t.persistence.notPersistentTitle}</h2>
            <p className="text-body mt-1 text-ink-5">
              {t.persistence.notPersistentBody}
            </p>
          </>
        )}
      </motion.aside>
    </AnimatePresence>
  );
}
